import type { CrawlView, FigureTile, PresentedReport } from "@shared/site-audit-report-view";
import { ActionList } from "./action-list";
import { CrawlPanel } from "./crawl-panel";
import { FindingList } from "./finding-list";
import { Freshness } from "./freshness";
import { LinksSection } from "./links-section";
import { PresentedCopy } from "./presented-text";
import { ScoreRing } from "./score-ring";
import { SearchSection } from "./search-section";
import { SecurityCoverage } from "./security-coverage";
import { SiteChecks } from "./site-checks";
import { SpeedCards } from "./speed-cards";
import { ReportFrame, ReportSection } from "./section";

export function AuditReport({
  report,
  brandName,
  logoUrl,
  dateLabel,
  includeEvidence = false,
  includeSecurity = false,
  titleLevel = "h2",
}: {
  report: PresentedReport;
  brandName: string;
  logoUrl?: string | null;
  dateLabel: string | null;
  includeEvidence?: boolean;
  includeSecurity?: boolean;
  /** Staff pages already have a page title. The public report is the page title. */
  titleLevel?: "h1" | "h2";
}) {
  const jumps = jumpLinks(report, includeEvidence, includeSecurity);
  const Title = titleLevel;
  return (
    <ReportFrame>
      <header className="flex flex-wrap items-end justify-between gap-4 border-b border-border pb-4">
        <div className="flex items-center gap-3">
          {logoUrl ? (
            <img src={logoUrl} alt="" referrerPolicy="no-referrer" className="h-10 w-auto max-w-[180px] object-contain" />
          ) : (
            <p className="font-heading text-lg font-semibold text-primary">{brandName}</p>
          )}
          {logoUrl ? <p className="text-sm text-foreground/75">{brandName}</p> : null}
        </div>
        <div className="text-right">
          <p className="text-xs font-medium uppercase tracking-wide text-foreground/75">Site audit</p>
          {dateLabel ? <p className="text-sm">{dateLabel}</p> : null}
        </div>
      </header>

      <div>
        <Title className="font-heading text-2xl font-semibold tracking-tight">{report.domain || "Site audit"}</Title>
      </div>

      {jumps.length > 0 ? (
        <nav aria-label="Report sections" className="flex gap-3 overflow-x-auto text-sm">
          {jumps.map((link) => (
            <a key={link.href} href={link.href} className="shrink-0 text-primary underline-offset-4 hover:underline">
              {link.label}
            </a>
          ))}
        </nav>
      ) : null}

      <section id="summary" className="scroll-mt-6 space-y-4">
        <h2 className="font-heading text-lg font-semibold tracking-tight">Verdict</h2>
        {report.verdict ? (
          <PresentedCopy value={report.verdict} className="max-w-3xl text-sm leading-relaxed" />
        ) : (
          <p className="text-sm text-foreground/75">The written summary is not ready yet.</p>
        )}
        <Freshness chips={report.freshness} />
        <ScoreRow report={report} />
      </section>

      {report.actions.length > 0 || report.quickWins.length > 0 || report.findings.length > 0 ? (
        <div className="grid gap-6 lg:grid-cols-2">
          {report.actions.length > 0 || report.quickWins.length > 0 ? (
            <ReportSection id="actions" title="Do this first" hint="Ordered from P0. A repeated quick win is shown once.">
              <ActionList actions={report.actions} quickWins={report.quickWins} />
            </ReportSection>
          ) : null}
          {report.findings.length > 0 ? (
            <ReportSection id="findings" title="What to fix" hint="Grouped by severity. A group with no rows is left out.">
              <FindingList groups={report.findings} />
            </ReportSection>
          ) : null}
        </div>
      ) : null}

      {!includeEvidence && report.search.competitiveAnalysis ? (
        <ReportSection id="competitive-analysis" title="Competitive analysis">
          <PresentedCopy value={report.search.competitiveAnalysis} className="max-w-3xl text-sm leading-relaxed whitespace-pre-line" />
        </ReportSection>
      ) : null}

      {includeEvidence ? (
        <>
          <CrawlPanel crawl={report.crawl} />
          <SpeedCards speed={report.speed} />
          <SearchSection search={report.search} />
          <LinksSection links={report.links} />
          <SiteChecks groups={report.checks} />
        </>
      ) : null}
      {includeSecurity ? <SecurityCoverage security={report.security} showCve={report.mode === "staff"} /> : null}

      {report.nextSteps ? (
        <ReportSection id="next-steps" title="Next steps">
          <PresentedCopy value={report.nextSteps} className="max-w-3xl text-sm leading-relaxed" />
        </ReportSection>
      ) : null}
    </ReportFrame>
  );
}

function ScoreRow({ report }: { report: PresentedReport }) {
  const { onPage, moz, traffic, keywords, speed, wordpress } = report.hero;
  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
      <div className="col-span-2 rounded-[0.75rem] border border-border bg-card p-4">
        <p className="text-xs font-medium text-foreground/75">On-page</p>
        <div className="mt-3">
          {onPage.kind === "scored" ? (
            <ScoreRing
              score={onPage.score}
              scoreLabel={onPage.scoreLabel}
              pages={onPage.pages}
              pagesLabel={onPage.pagesLabel}
              summary={onPage.summary}
            />
          ) : (
            <CrawlEmpty crawl={onPage} />
          )}
        </div>
      </div>
      <MetricTile tile={moz} />
      <MetricTile tile={traffic} />
      <MetricTile tile={keywords} />
      <MetricTile tile={speed} />
      <div className="col-span-2 lg:col-span-3">
        <MetricTile tile={wordpress} />
      </div>
    </div>
  );
}

function CrawlEmpty({ crawl }: { crawl: Exclude<CrawlView, { kind: "scored" }> }) {
  return (
    <div>
      <p className="text-base font-medium">{crawl.heading}</p>
      {"detail" in crawl && crawl.detail ? (
        <p className="mt-1 text-sm text-foreground/75">{crawl.detail}</p>
      ) : null}
    </div>
  );
}

function MetricTile({ tile }: { tile: FigureTile }) {
  const empty = tile.state === "empty";
  return (
    <div className="rounded-[0.75rem] border border-border bg-card p-4">
      <p className="text-xs font-medium text-foreground/75">{tile.label}</p>
      <p
        className={`mt-2 font-mono font-semibold tabular-nums leading-tight ${
          empty ? "text-lg" : "text-[26px]"
        } ${tile.tone === "problem" ? "text-destructive" : tile.tone === "good" ? "text-chart-2" : ""}`}
      >
        {empty ? tile.emptyLabel : tile.value}
      </p>
      {tile.note ? <p className="mt-2 text-xs text-foreground/75">{tile.note}</p> : null}
    </div>
  );
}

function jumpLinks(
  report: PresentedReport,
  evidence: boolean,
  security: boolean,
): { href: string; label: string }[] {
  const links: { href: string; label: string }[] = [{ href: "#summary", label: "Summary" }];
  if (report.actions.length > 0 || report.quickWins.length > 0) links.push({ href: "#actions", label: "Do this first" });
  if (report.findings.length > 0) links.push({ href: "#findings", label: "Issues" });
  if (evidence) {
    links.push({ href: "#crawl", label: "Crawl" });
    links.push({ href: "#speed", label: "Speed" });
    links.push({ href: "#search", label: "Search" });
    links.push({ href: "#links", label: "Links" });
    if (report.checks.length > 0) links.push({ href: "#checks", label: "Checks" });
  }
  if (security) links.push({ href: "#wordpress", label: "WordPress" });
  if (report.nextSteps) links.push({ href: "#next-steps", label: "Next steps" });
  return links;
}
